import React, { useState } from "react";
import AccordionEditable from "../../../../../components/ui/accordions/AccordionEditable";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import ExpertDocumentList from "../../../../../components/ui/forms/ExpertDocumentList";
import { updateDocument } from "../../../../../api/filings/documentsAPI";
import { partialUpdateFiling } from "../../../../../api/filings/filingAPI";
import ConfirmationModal from "../../../../../components/ui/popup/ConfirmationModal";
import Tooltip from "../../../../../components/ui/Tooltip";
import ModalNew from "../../../../../components/ui/popup/Modal";
import { PopupHeader } from "../../../../../components/ui/popup/common";
import SmartButton from "../../../../../components/ui/buttons/SmartButton";

const ExpertDocumentsSection = ({
  filing,
  setFiling,
  filingUuid,
  documents,
  isCpa,
  allTaxSituationComplete,
  handleOpenUploadModal,
  handleDocumentDeleted,
  handleDocumentUpdated,
}) => {
  // --- State Management ---
  const [showMoveToSignConfirmation, setShowMoveToSignConfirmation] =
    useState(false);
  const [documentToMove, setDocumentToMove] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const [showMarkDoneConfirmation, setShowMarkDoneConfirmation] =
    useState(false);

  // POA Amount Logic
  const [isSettingAmount, setIsSettingAmount] = useState(false);
  const [poaAmount, setPoaAmount] = useState(filing.amount_of_poa || 0);
  const [showPoaAmountConfirmation, setShowPoaAmountConfirmation] =
    useState(false);

  const [showSetAmountModal, setShowSetAmountModal] = useState(false);

  // --- Summary Metrics ---
  const metrics = filing?.summary_metrics?.expert_documents;

  const allActionsComplete =
    !!metrics?.complete && !!metrics?.all_efile_complete;

  const hasRequiredActions =
    !allActionsComplete &&
    (isCpa ? !!metrics?.attention_needed_cpa : !!metrics?.attention_needed_cus);

  const isWaiting = !allActionsComplete && !hasRequiredActions;

  // --- Document Filtering ---
  const reviewDocs = documents.filter(
    (doc) =>
      ["EFILE_FEDERAL", "EFILE_STATE"].includes(
        doc.documentType?.toUpperCase(),
      ) && !["ESN", "ESD"].includes(doc.status),
  );

  const signatureDocs = documents.filter((doc) =>
    ["ESN", "ESD"].includes(doc.status),
  );

  const poaDocs = documents.filter(
    (doc) => doc.documentType?.toUpperCase() === "PROOF_OF_ACCEPTANCE",
  );

  const poaUnblocked = filing.all_doc_signatures_completed === true;

  // --- Handlers ---
  const handleConfirmMove = async () => {
    if (!documentToMove || isMoving) return;
    setIsMoving(true);
    try {
      const data = { status: "ESN", updateType: "move_to_signature" };
      const response = await updateDocument(
        "tax/document",
        documentToMove.documentId,
        data,
        { includeMetrics: true },
      );
      if (response.ok) {
        setShowMoveToSignConfirmation(false);
        setDocumentToMove(null);
        const resData = await response.json();
        if (handleDocumentUpdated)
          handleDocumentUpdated(resData.summary_metrics);
      }
    } catch (err) {
      console.error("Error moving document:", err);
    } finally {
      setIsMoving(false);
    }
  };

  const handleMarkSignaturesDone = async () => {
    try {
      const response = await partialUpdateFiling(
        "tax/filing",
        { all_doc_signatures_completed: true },
        filingUuid,
        { includeMetrics: true },
      );

      if (response.ok) {
        const resData = await response.json();
        handleDocumentUpdated(resData.summary_metrics);
        setFiling((prev) => ({
          ...prev,
          all_doc_signatures_completed: true,
          summary_metrics: resData.summary_metrics,
        }));
      }
    } catch (err) {
      console.error("Critical error marking signatures done:", err);
    }
  };

  const handleConfirmSavePoaAmount = async () => {
    await handleSavePoaAmount();
    setShowPoaAmountConfirmation(false);
  };

  const handleSavePoaAmount = async () => {
    try {
      // Ensure we are sending the current local state 'poaAmount'
      const response = await partialUpdateFiling(
        "tax/filing",
        { amount_of_poa: poaAmount },
        filingUuid,
        { includeMetrics: true },
      );

      if (response.ok) {
        const resData = await response.json();

        // Update the parent state immediately so the UI reflects the change
        setFiling((prev) => ({
          ...prev,
          amount_of_poa: poaAmount, // Sync the root field
          summary_metrics: resData.summary_metrics, // Sync the badges
        }));

        handleDocumentUpdated(resData.summary_metrics);
        setIsSettingAmount(false);
        setShowPoaAmountConfirmation(false);
      }
    } catch (err) {
      console.error("Error saving POA amount:", err);
    }
  };

  return (
    <AccordionEditable
      title="Expert Documents"
      isBlocked={!allTaxSituationComplete}
      disabled={!allTaxSituationComplete}
      hasRequiredActions={hasRequiredActions}
      allActionsComplete={allActionsComplete}
      isWaiting={isWaiting}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* SECTION 1: DRAFTS */}
        <ContentCard>
          <div className="section-header-row">
            <h4 className="section-header-title">Documents for Review</h4>
            <Tooltip
              title="Draft Workspace"
              content="Collaborate on draft documents prior to approval and signature."
            />
            {isCpa && (
              <div className="section-header-upload">
                <button
                  className="cta-button primary"
                  disabled={allActionsComplete}
                  onClick={() =>
                    handleOpenUploadModal({
                      name: "EFILE_FEDERAL",
                      label: "Upload Draft",
                    })
                  }
                >
                  Upload Draft
                </button>
              </div>
            )}
          </div>
          <ExpertDocumentList
            documents={reviewDocs}
            isCpa={isCpa}
            showAcceptReject={!isCpa}
            onDocumentUpdated={handleDocumentUpdated}
            onDocumentDeleted={handleDocumentDeleted}
            onOpenUploadModal={handleOpenUploadModal}
            customActions={
              isCpa
                ? [
                    {
                      label: "Request Signature",
                      onClick: (d) => {
                        setDocumentToMove(d);
                        setShowMoveToSignConfirmation(true);
                      },
                      showIf: (d) => d.status === "ACC",
                    },
                  ]
                : []
            }
          />
        </ContentCard>

        {/* SECTION 2: SIGNATURES */}
        <ContentCard>
          <div className="section-header-row">
            <h4 className="section-header-title">Signature Authorization</h4>
            <Tooltip
              title="Signature & Authorization"
              content="Documents that require electronic signature before we can proceed with filing."
            />
          </div>
          <ExpertDocumentList
            documents={signatureDocs}
            isCpa={isCpa}
            onOpenUploadModal={
              !isCpa ? (source, id) => handleOpenUploadModal(source, id) : null
            }
            onDocumentUpdated={handleDocumentUpdated}
          />
          {isCpa &&
            signatureDocs.length > 0 &&
            signatureDocs.every((d) => d.status === "ESD") && (
              <button
                className={`cta-button full-width ${
                  poaUnblocked ? "secondary" : "primary"
                }`}
                onClick={() =>
                  !poaUnblocked && setShowMarkDoneConfirmation(true)
                }
                disabled={poaUnblocked}
                style={{
                  marginTop: "10px",
                  cursor: poaUnblocked ? "default" : "pointer",
                }}
              >
                {poaUnblocked ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/award_star.svg`}
                      style={{ width: "16px" }}
                      alt="complete"
                    />
                    All Signatures Completed
                  </span>
                ) : (
                  "Mark All Signatures Completed"
                )}
              </button>
            )}
        </ContentCard>

        {/* SECTION 3: POA */}
        <ContentCard>
          <div className="section-header-row">
            <h4 className="section-header-title">Proof of Acceptance</h4>
            <Tooltip
              title="Return Acceptance Documents"
              content="Official confirmation that your filing has been received and accepted."
            />

            {isCpa && poaUnblocked && !allActionsComplete && (
              <div className="section-header-upload">
                <button
                  className="cta-button primary"
                  disabled={!filing.amount_of_poa || filing.amount_of_poa === 0}
                  onClick={() =>
                    handleOpenUploadModal({ name: "PROOF_OF_ACCEPTANCE" })
                  }
                  title={
                    !filing.amount_of_poa ? "Set count before uploading" : ""
                  }
                >
                  Upload POA
                </button>
              </div>
            )}
          </div>

          {poaUnblocked && (
            <div
              className="section-meta-row"
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <div className="section-meta-item">
                <span className="section-meta-label">Required: </span>
                <span className="section-meta-value">
                  <strong>{filing.amount_of_poa || 0}</strong>
                </span>
              </div>
              <div className="section-meta-item">
                <span className="section-meta-label">Provided: </span>
                <span className="section-meta-value">
                  <strong>{poaDocs.length}</strong>
                </span>
              </div>
              {isCpa && !allActionsComplete && (
                <div className="section-meta-item">
                  <button
                    className="cta-button secondary"
                    style={{ padding: "4px 10px", fontSize: "12px" }}
                    onClick={() => setShowSetAmountModal(true)} // This triggers the custom UI above
                  >
                    {filing.amount_of_poa > 0 ? "Edit Count" : "Set Count"}
                  </button>
                </div>
              )}
            </div>
          )}

          {!poaUnblocked && (
            <p style={{ color: "#adb0b2", fontSize: "12px", margin: "10px 0" }}>
              Waiting for signatures to be completed.
            </p>
          )}
          <ExpertDocumentList documents={poaDocs} isCpa={isCpa} />
        </ContentCard>
      </div>

      <ConfirmationModal
        isOpen={showMoveToSignConfirmation}
        onClose={() => !isMoving && setShowMoveToSignConfirmation(false)}
        onConfirm={handleConfirmMove}
        title="Request Signature"
        message="Move this document to Signature Authorization? The taxpayer will be notified."
        confirmText={isMoving ? "Sending..." : "Request"}
        cancelText="Cancel"
        confirmButtonClass="primary"
        isLoading={isMoving}
      />

      <ConfirmationModal
        isOpen={showMarkDoneConfirmation}
        onClose={() => setShowMarkDoneConfirmation(false)}
        onConfirm={async () => {
          await handleMarkSignaturesDone();
          setShowMarkDoneConfirmation(false);
        }}
        title="Confirm Completion"
        message="Mark all signatures as complete? This enables final processing and POA uploads."
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonClass="primary"
      />

      <ConfirmationModal
        isOpen={showPoaAmountConfirmation}
        onClose={() => setShowPoaAmountConfirmation(false)}
        onConfirm={handleSavePoaAmount}
        title="Confirm Amount"
        message={`You are setting the requirement to ${poaAmount} Proof of Acceptance document(s). Is this correct?`}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      {/* --- Set POA Amount Modal --- */}
      {showSetAmountModal && (
        <ModalNew className="confirmation-modal">
          {/* Using your standard PopupHeader */}
          <PopupHeader
            title="Set POA Requirement"
            setOpenState={() => setShowSetAmountModal(false)}
            hideCloseButton={true}
          />

          <div className="confirmation-modal-form">
            <div
              className="confirmation-content"
              style={{ textAlign: "center" }}
            >
              <p style={{ marginBottom: "12px" }}>
                How many Proof of Acceptance documents are required for this
                filing?
              </p>

              <input
                type="number"
                min="1"
                value={poaAmount}
                onChange={(e) => setPoaAmount(parseInt(e.target.value) || 0)}
                style={{
                  width: "100px",
                  padding: "10px",
                  textAlign: "center",
                  fontSize: "20px",
                  border: "2px solid #007bff",
                  borderRadius: "4px",
                  margin: "10px auto 20px auto",
                  display: "block",
                }}
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <SmartButton
                type="button"
                className="cta-button secondary"
                onClick={() => setShowSetAmountModal(false)}
                text="Cancel"
              />
              <SmartButton
                type="button"
                className="cta-button primary"
                disabled={poaAmount < 1}
                onClick={() => {
                  setShowSetAmountModal(false);
                  setShowPoaAmountConfirmation(true);
                }}
                text="Next"
              />
            </div>
          </div>
        </ModalNew>
      )}
    </AccordionEditable>
  );
};

export default ExpertDocumentsSection;
